import sha256 from 'crypto-js/sha256'

const BAR = '|'
const FINGERPRINT_COOKIE_NAME = 'PLM.fgpid'

// Get screen color and pixel depth
const getScreenPrint = (): string => {
  return `Color Depth: ${screen.colorDepth}, Pixel Depth: ${screen.pixelDepth}`
}

// Get installed plugins from the navigator
const getPlugins = (): string => {
  if (!navigator.plugins) return ''
  return Array.from(navigator.plugins)
    .map(plugin => plugin.name)
    .join(', ')
}

// Get the 2D canvas context or return null if unavailable
const getCanvasContext = (canvas: HTMLCanvasElement): CanvasRenderingContext2D | null => {
  try {
    return canvas.getContext('2d')
  }
  catch {
    return null
  }
}

// Generate a canvas fingerprint by drawing and encoding the canvas as a data URL
const getCanvasPrint = (): string => {
  const canvas = document.createElement('canvas')
  const ctx = getCanvasContext(canvas)
  // Return specific drawing data or characteristics of the canvas if needed
  return ctx ? canvas.toDataURL() : ''
}

// Generate a hash-based fingerprint using various browser and device attributes
const generateFingerprint = (
  storage: 'localStorage' | 'sessionStorage',
  timeZone: string,
): string => {
  const key = [
    navigator.userAgent,
    location.hostname,
    getScreenPrint(),
    getPlugins(),
    storage,
    timeZone,
    navigator.language,
    String(navigator.cookieEnabled),
    getCanvasPrint(),
  ].join(BAR)

  const hash = sha256(key)
  return hash.toString()
}

// Set a cookie with various options like path, expiry, security, and SameSite attributes
const setCookie = (
  name: string,
  value: string,
  days: number,
  path: string = '/',
  secure: boolean = true,
  httpOnly: boolean = false,
  sameSite: 'Strict' | 'Lax' | 'None' = 'Strict',
): void => {
  let cookieString = `${name}=${encodeURIComponent(value)}; path=${path};`
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000) // Convert to milliseconds
    cookieString += ` expires=${date.toUTCString()};`
  }
  if (secure) cookieString += ' secure;'
  if (httpOnly) cookieString += ' HttpOnly;'
  cookieString += ` SameSite=${sameSite};`
  document.cookie = cookieString
}

// Get a cookie value by its name, or return null if not found
const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`
  const cookiesArray = document.cookie ? document.cookie.split(';') : []
  for (let cookie of cookiesArray) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length))
    }
  }
  return null
}

// Main function to get or generate the fingerprint and store it in a cookie
export const getFingerprint = (
  storage: 'localStorage' | 'sessionStorage',
  timeZone: string,
): string => {
  let fingerPrint = getCookie(FINGERPRINT_COOKIE_NAME)

  if (!fingerPrint) {
    const generatedFingerprint = generateFingerprint(storage, timeZone)
    setCookie(FINGERPRINT_COOKIE_NAME, generatedFingerprint, 365)
    return generatedFingerprint
  }

  return fingerPrint
}
